import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = { donation_type_id: '', name: '', amount: '' }

function DonationTypePage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', {}, 'donation_type/donationTypes')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load donation types.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const closeForm = () => setShowForm(false)

  const openNew = () => {
    setForm(EMPTY)
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      donation_type_id: row.donation_type_id || '',
      name: row.name || '',
      amount: row.amount ?? '',
    })
    setShowForm(true)
    setMessage(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const payload = {
      donation_type_id: form.donation_type_id || '',
      name: form.name.trim(),
      amount: form.amount === '' ? 0 : Number(form.amount),
    }
    const data = await PostAction('post', payload, 'donation_type/addDonationType')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Saved.' })
      setShowForm(false)
      await load()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save.' })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'name', label: 'Name' },
    { key: 'amount', label: 'Amount' },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => openEdit(row)}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="donation_type">
      <PageHeader
        title="Donation Type"
        subtitle="Preset donation categories and default amounts."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Type
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable columns={columns} rows={rows} searchKeys={['name', 'amount']} />
        )}
      </div>

      <Modal
        show={showForm}
        onHide={closeForm}
        backdrop="static"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {form.donation_type_id ? 'Edit Donation Type' : 'Add Donation Type'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="manage-donation-type-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="dt_name">
                  Type Name
                </label>
                <input
                  id="dt_name"
                  className="form-control"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="dt_amount">
                  Default Amount
                </label>
                <input
                  id="dt_amount"
                  type="number"
                  min="0"
                  step="1"
                  className="form-control"
                  required
                  value={form.amount}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manage-donation-type-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default DonationTypePage
